import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Switch } from '@mui/material';
import { settingsApi } from '@/services/api';
import type { Setting } from '@/types/client';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';
import { Button, Pill, Tag } from '@/components/ui';
import { formatDate } from '@/utils/formatters';
import shared from '@/components/utils/shared.module.css';
import styles from './SettingsMaster.module.css';

// field_type: 1=Text 2=Boolean 3=Number 4=JSON 5=Secret 6=Date
const FIELD_VALIDATORS: Record<number, (value: string) => string | null> = {
  1: () => null,
  2: () => null,
  3: (v) => v.trim() === '' || isNaN(Number(v)) ? 'Must be a valid number' : null,
  4: (v) => { try { JSON.parse(v); return null; } catch { return 'Must be valid JSON'; } },
  5: (v) => v.trim() === '' ? 'Enter a new value to update' : null,
  6: (v) => v && isNaN(Date.parse(v)) ? 'Must be a valid date' : null,
};

const SettingsMaster: React.FC = () => {
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [draftValue, setDraftValue] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);
  const [sortField, setSortField] = useState('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    settingsApi.getSettings()
      .then(data => { if (!cancelled) setSettings(data); })
      .catch(() => { if (!cancelled) toast.error('Failed to load settings'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const handleEdit = useCallback((setting: Setting) => {
    setEditingId(setting.id);
    if (setting.field_type === 5) {
      // Secret: always start blank so the user must retype — never pre-fill "****"
      setDraftValue('');
    } else if (setting.field_type === 2) {
      // Boolean: normalise to 'true'/'false' — backend may return '' for a new setting
      setDraftValue(setting.value === 'true' ? 'true' : 'false');
    } else {
      // field_type 6 (Date): backend must return ISO-8601 (YYYY-MM-DD) so <input type="date"> pre-fills correctly
      setDraftValue(setting.value);
    }
  }, []);

  const handleCancel = useCallback(() => {
    setEditingId(null);
    setDraftValue('');
  }, []);

  const handleApply = useCallback(async (setting: Setting, draft: string) => {
    setSavingId(setting.id);
    try {
      const updated = await settingsApi.updateSetting(setting.id, draft);
      setSettings(prev => prev.map(s => s.id === setting.id ? updated : s));
      toast.success(`"${setting.name}" updated`);
      setEditingId(null);
      setDraftValue('');
    } catch {
      toast.error(`Failed to update "${setting.name}"`);
    } finally {
      setSavingId(null);
    }
  }, []);

  const handleSort = useCallback((field: string) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }, [sortField]);

  const sortedSettings = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = query
      ? settings.filter(s =>
          s.name.toLowerCase().includes(query) || s.code.toLowerCase().includes(query))
      : settings;
    return [...matching].sort((a, b) => {
      const av = String(a[sortField as keyof Setting] ?? '').toLowerCase();
      const bv = String(b[sortField as keyof Setting] ?? '').toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [settings, search, sortField, sortDir]);

  const renderEditInput = useCallback((setting: Setting, saving: boolean) => {
    const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setDraftValue(e.target.value);

    switch (setting.field_type) {
      case 2:
        return (
          <div className={styles['toggle-wrapper']}>
            <Switch
              checked={draftValue === 'true'}
              onChange={e => setDraftValue(e.target.checked ? 'true' : 'false')}
              disabled={saving}
              size="small"
              color="primary"
            />
            <span className={styles['toggle-label']}>{draftValue === 'true' ? 'true' : 'false'}</span>
          </div>
        );
      case 3:
        return (
          <input
            type="number"
            value={draftValue}
            onChange={onChange}
            disabled={saving}
            className={styles['edit-input']}
            autoFocus
          />
        );
      case 4:
        return (
          <textarea
            value={draftValue}
            onChange={onChange}
            disabled={saving}
            className={`${styles['edit-input']} ${styles['edit-textarea']}`}
            rows={3}
            autoFocus
          />
        );
      case 5:
        return (
          <input
            type="password"
            value={draftValue}
            onChange={onChange}
            disabled={saving}
            className={styles['edit-input']}
            placeholder="Enter new value"
            autoFocus
          />
        );
      case 6:
        return (
          <input
            type="date"
            value={draftValue}
            onChange={onChange}
            disabled={saving}
            className={styles['edit-input']}
            autoFocus
          />
        );
      default:
        return (
          <input
            type="text"
            value={draftValue}
            onChange={onChange}
            disabled={saving}
            className={styles['edit-input']}
            autoFocus
          />
        );
    }
  }, [draftValue]);

  const columns = useMemo<ColumnDef<Setting>[]>(() => [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '22%',
      render: (row) => <span className={shared['cell-strong']}>{row.name}</span>,
    },
    {
      key: 'code',
      header: 'Code',
      sortable: true,
      width: '22%',
      render: (row) => <Tag>{row.code}</Tag>,
    },
    {
      key: 'value',
      header: 'Value',
      sortable: false,
      width: '28%',
      render: (row) => {
        if (editingId === row.id) {
          const validator = FIELD_VALIDATORS[row.field_type] ?? FIELD_VALIDATORS[1];
          const validationError = validator(draftValue);
          const unchanged = row.field_type !== 5 && draftValue === row.value;
          const saving = savingId === row.id;
          return (
            <form
              className={styles['edit-container']}
              onSubmit={(e) => {
                e.preventDefault();
                if (!validationError && !unchanged && !saving) handleApply(row, draftValue);
              }}
            >
              {renderEditInput(row, saving)}
              {validationError && <div className={styles['validation-error']}>{validationError}</div>}
              <div className={styles['edit-actions']}>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  disabled={!!validationError || unchanged || saving}
                >
                  {saving ? 'Saving…' : 'Apply'}
                </Button>
                <Button type="button" size="sm" onClick={handleCancel} disabled={saving}>
                  Cancel
                </Button>
              </div>
            </form>
          );
        }
        const displayValue = () => {
          if (row.field_type === 5) return <span className={styles['secret-mask']}>••••••••</span>;
          if (row.field_type === 2) {
            // A boolean setting is on or off, not good or bad — only "on" earns the
            // positive tone; "off" stays neutral rather than reading as an error.
            return row.value === 'true'
              ? <Pill tone="positive">true</Pill>
              : <Pill tone="neutral">false</Pill>;
          }
          if (row.field_type === 6) {
            return row.value
              ? <span>{formatDate(row.value)}</span>
              : <span className={styles['empty-value']}>—</span>;
          }
          return row.value
            ? <span>{row.value}</span>
            : <span className={styles['empty-value']}>—</span>;
        };
        return (
          <div
            className={styles['value-display']}
            onClick={() => handleEdit(row)}
            role="button"
            aria-label={`Edit ${row.name}`}
            tabIndex={0}
            onKeyDown={e => e.key === 'Enter' && handleEdit(row)}
          >
            <span className={styles['value-text']}>{displayValue()}</span>
            <span className={styles['edit-hint']}>✎</span>
          </div>
        );
      },
    },
    {
      key: 'field_type_display',
      header: 'Type',
      sortable: true,
      width: '12%',
      render: (row) => <Tag>{row.field_type_display}</Tag>,
    },
    {
      key: 'updated_ts',
      header: 'Last Updated',
      sortable: true,
      width: '16%',
      render: (row) => <span className={styles['date-cell']}>{formatDate(row.updated_ts)}</span>,
    },
  ], [editingId, draftValue, savingId, renderEditInput, handleEdit, handleApply, handleCancel]);

  return (
    <DataTable
      title="Settings"
      subtitle="Application-wide configuration. Click a value to edit it in place."
      data={sortedSettings}
      columns={columns}
      rowKey={r => r.id}
      totalCount={sortedSettings.length}
      loading={loading}
      currentPage={1}
      totalPages={1}
      onPageChange={() => { /* single-page — never called */ }}
      pageSize={sortedSettings.length || 20}
      sortField={sortField}
      sortDir={sortDir}
      onSort={handleSort}
      searchQuery={search}
      onSearchChange={setSearch}
      searchPlaceholder="Search by name or code…"
      emptyMessage={search ? 'No settings match that search.' : 'No settings found.'}
      fitContent
    />
  );
};

export default SettingsMaster;
